import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signIn, signOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

import { AuthenticatedAccount } from '@client:common/AuthenticatedAccount';

export class SessionManagerService {
  private static readonly _INSTANCE_ = new SessionManagerService();

  static getInstance() {
    return SessionManagerService._INSTANCE_;
  }

  private constructor() {
    this._configureCognitoClient();
  }

  private _configureCognitoClient() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: 'us-west-2_rs1kzCYNP',
          userPoolClientId: '5d82n4n97futur211inqdlmsuq',
          loginWith: {
            email: true
          }
        }
      }
    });
  }

  async signIn(email: string, password: string): Promise<AuthenticatedAccount> {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password
      });

      if (isSignedIn) {
        // Get session to extract user details
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;

        // Get user attributes
        const attributes = await fetchUserAttributes();
        const groups = accessToken?.payload?.["cognito:groups"] as string[] | undefined;

        return {
          email: attributes.email || email,
          firstName: attributes['custom:firstName'] || email.split('.')[0],
          lastName: attributes['custom:lastName'] || email.split('.')[1],
          organization: attributes['custom:organization'],
          accessToken: accessToken?.toString() || '',
          isVerified: true,
          groups
        };
      } else {
        return Promise.reject(`Authentication incomplete: ${nextStep.signInStep}`);
      }
    } catch (error: any) {
      return Promise.reject(
        (typeof error === 'string' ? error : error.message).toLowerCase()
      );
    }
  }

  async signOut() {
    try {
      await signOut();
      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(
        (typeof error === 'string' ? error : error.message).toLowerCase()
      );
    }
  }

  async restoreSession(): Promise<AuthenticatedAccount> {
    try {
      // First check if user is authenticated
      await getCurrentUser();

      // Get user attributes
      const attributes = await fetchUserAttributes();

      // Get session tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;

      const groups = accessToken?.payload["cognito:groups"] as string[] | undefined;

      if (!accessToken) {
        throw new Error("No active session");
      }

      return {
        email: attributes.email || '',
        firstName: attributes['custom:firstName'] ||
          (attributes.username ? attributes.username.split('.')[0] : ''),
        lastName: attributes['custom:lastName'] ||
          (attributes.username ? attributes.username.split('.')[1] : ''),
        organization: attributes['custom:organization'],
        accessToken: accessToken.toString(),
        isVerified: true,
        groups
      };
    } catch (reason: any) {
      return Promise.reject((typeof reason === 'string' ? reason : reason.message).toLowerCase());
    }
  }
}
